/**
 * 🚀 [Phase v13.51 — 2026-07-14] Sujet 4.E — Dialog "Sections incomplètes"
 *
 * Affiché quand un clic sur "Marquer chantier complété" retourne 409 avec la liste
 * des sections manquantes. L'utilisatrice peut alors :
 *   - Cliquer "Retour au BT" → ferme le dialog et navigue vers /crm/projets/{did}
 *   - Cliquer "Retirer quand même" → réémet la requête avec force=true
 */

import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle } from "lucide-react";

export function MarquerChantierCompleteDialog({
  open,
  onOpenChange,
  sectionsManquantes,
  clientNom,
  onRetourAuBT,
  onRetirerQuandMeme,
  loading,
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="dialog-sections-incompletes" className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-amber-900">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Sections incomplètes
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm text-slate-700">
              <p>
                Ce chantier <strong>{clientNom}</strong> a été marqué comme complété, mais
                certaines sections sont encore incomplètes :
              </p>
              <ul
                data-testid="liste-sections-manquantes"
                className="list-disc list-inside space-y-1 pl-2 bg-amber-50 border border-amber-200 rounded-md p-3"
              >
                {(sectionsManquantes || []).map((s) => (
                  <li key={s.code} data-testid={`section-manquante-${s.code}`} className="text-amber-900">
                    {s.label_fr}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-slate-500 italic">
                Vous pouvez retourner au BT pour compléter les sections manquantes ou forcer
                le retrait du tableau (le chantier sera archivé malgré tout).
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            data-testid="btn-retour-au-bt"
            onClick={onRetourAuBT}
            disabled={loading}
          >
            Retour au BT
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid="btn-retirer-quand-meme"
            onClick={onRetirerQuandMeme}
            disabled={loading}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {loading ? "Retrait en cours…" : "Retirer quand même"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * Hook custom : gère l'état du dialog + les appels API.
 *
 * Retourne :
 *   - dialogState : { open, sectionsManquantes, dossierId, clientNom }
 *   - marquerComplet(dossier) : lance l'action
 *   - closeDialog(), retirerQuandMeme()
 */
export function useMarquerChantierComplete({ apiUrl, axios, onSuccess, onNavigate, toast }) {
  const [dialogState, setDialogState] = useState({
    open: false,
    sectionsManquantes: [],
    dossierId: null,
    clientNom: "",
  });
  const [loading, setLoading] = useState(false);

  const marquerComplet = async (dossier) => {
    if (!dossier || !dossier.dossier_id) return;
    setLoading(true);
    try {
      await axios.post(
        `${apiUrl}/api/crm/dossiers/${dossier.dossier_id}/marquer-chantier-complete`,
        {}
      );
      toast.success(`Chantier "${dossier.client_nom || dossier.nom_dossier}" retiré du tableau`);
      onSuccess?.();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409 && detail?.code === "sections_incompletes") {
        setDialogState({
          open: true,
          sectionsManquantes: detail.sections_manquantes || [],
          dossierId: dossier.dossier_id,
          clientNom: dossier.client_nom || dossier.nom_dossier || "",
        });
      } else {
        toast.error("Impossible de marquer le chantier comme complété (voir console).");
        console.error("[marquer-chantier-complete] error:", err);
      }
    } finally {
      setLoading(false);
    }
  };

  const retirerQuandMeme = async () => {
    if (!dialogState.dossierId) return;
    setLoading(true);
    try {
      await axios.post(
        `${apiUrl}/api/crm/dossiers/${dialogState.dossierId}/marquer-chantier-complete?force=true`,
        {}
      );
      toast.success(`Chantier "${dialogState.clientNom}" retiré (retrait forcé)`);
      setDialogState({ open: false, sectionsManquantes: [], dossierId: null, clientNom: "" });
      onSuccess?.();
    } catch (err) {
      toast.error("Erreur lors du retrait forcé (voir console).");
      console.error("[marquer-chantier-complete force] error:", err);
    } finally {
      setLoading(false);
    }
  };

  const closeDialog = () => {
    setDialogState({ open: false, sectionsManquantes: [], dossierId: null, clientNom: "" });
  };

  const retourAuBT = () => {
    const did = dialogState.dossierId;
    closeDialog();
    if (did && onNavigate) onNavigate(`/crm/projets/${did}`);
  };

  return {
    dialogState,
    loading,
    marquerComplet,
    retirerQuandMeme,
    closeDialog,
    retourAuBT,
  };
}
